import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Edit2Icon } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import { GameProviderWithBannerListTableData } from '@/modules/game/type'
import useMutationUpdateProvider from '@/modules/game/api/hooks/useMutationUpdateProvider'
import { Label } from '@/common/ui/label'
import { Input } from '@/common/ui/input'
import { RtpPresetPrecedenceTooltip } from '@/common/components/RtpPresetPrecedenceTooltip'

interface EditDefaultPresetNumberProps {
	row: GameProviderWithBannerListTableData
}

export default function EditDefaultPresetNumber({
	row,
}: EditDefaultPresetNumberProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [presetNumber, setPresetNumber] = useState<string>(
		row.defaultPresetNumber?.toString() || '',
	)
	const { mutateAsync: updateProvider, isPending } = useMutationUpdateProvider()
	const queryClient = useQueryClient()

	const displayValue = row.defaultPresetNumber
		? `${row.defaultPresetNumber}%`
		: '-'

	const handleUpdatePreset = async () => {
		try {
			await updateProvider({
				id: row.providerId,
				payload: {
					defaultPresetNumber: presetNumber ? Number(presetNumber) : undefined,
				},
			})
			queryClient.invalidateQueries({
				queryKey: [GameQueryKeys.GetGameProviderList],
			})
			toast.success('อัพเดท RTP ตั้งต้น สำเร็จ')
			setIsOpen(false)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleClose = () => {
		setIsOpen(false)
		setPresetNumber(row.defaultPresetNumber?.toString() || '')
	}

	return (
		<>
			<div className="flex items-center gap-2">
				<span className="text-sm">{displayValue}</span>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8"
					onClick={() => setIsOpen(true)}
				>
					<Edit2Icon className="h-4 w-4" />
				</Button>
			</div>

			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							แก้ไข RTP ตั้งต้น - {row.displayName}
							<RtpPresetPrecedenceTooltip highlightLevel={6} />
						</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>ตั้งค่าเปอร์เซ็น RTP (30-99)</Label>
							<Input
								type="number"
								min={30}
								max={99}
								placeholder="ระบุเปอร์เซ็น RTP (30-99)"
								value={presetNumber}
								onChange={(e) => setPresetNumber(e.target.value)}
								disabled={isPending}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							ยกเลิก
						</Button>
						<Button onClick={handleUpdatePreset} disabled={isPending}>
							{isPending ? 'กำลังบันทึก...' : 'บันทึก'}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
